// ** React Imports
import { useEffect } from "react";
import { useLocation, useHistory } from "react-router-dom";

// ** Redux & Store & Actions
import { useDispatch, useSelector, connect } from "react-redux";
import {
	closeRegistrationVerifyEmailModal,
	setIsAuth,
	setLanguageAsync,
} from "store/modules/global-common.slice";
import { getUser } from "store/modules/global-user/actions/get-user.action";
import { resetHomepage } from "store/modules/main-pages/home/home.slice";
import { resetSharedFilters } from "store/modules/shared-filters.slice";

// ** Third Party Components
import md5 from "md5";
import { toast } from "helpers/toastWrapper";
import queryString from "query-string";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

// ** Custom Hooks
import { useFingerprint } from "hooks/useFingerprint";

// ** Utils & Helpers
import api from "helpers/ApiClient";

// ** Routes
import routes from "containers";

// ** Custom Components
import { LoaderOAuth } from "containers/Oauth/components/LoaderOAuth";
import { loginGoogle } from "store/modules/auth/auth-login.slice";
import { trackUserRegistrationRedirectAfterOAuth } from "helpers/analytics";

export const StyledFallbackContainerOAuth = styled.div`
	display: flex;
	align-self: center;
	min-height: 100vh;
	width: 100%;
	font-family: Arial, Helvetica, sans-serif;
	font-size: 14px;
	line-height: 1.5;
	background-color: ${({ $isDarkMode }) =>
		$isDarkMode ? "#202020" : "#fff"};
`;
export const StyledMessageContainerOAuth = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	width: 100%;
	text-align: center;
	line-height: 2.5rem;
	margin: auto;
`;

const StyledLogo = styled.svg`
	display: block;
	fill: ${({ $isDarkMode }) =>
		$isDarkMode
			? "var(--dz-ref-color-white)"
			: "var(--color-primary-always)"};

	paint-order: stroke markers fill;
`;

export const DatezoneLogoOAuth = ({ isDarkMode }) => {
	return (
		<StyledLogo
			xmlns="http://www.w3.org/2000/svg"
			xmlSpace="preserve"
			viewBox="0 0 199.91 49.81"
			width={130}
			height={34}
			$isDarkMode={isDarkMode}
		>
			<path d="M107.19 17.93h8.58l-10.12 17.7h18.14v-4.77h-9.5c3.54-6.23 6.27-11 10.1-17.7h-17.2zm69.26 6.58c.1 6.7 4.87 11.62 11.94 11.62 4.52 0 7.3-1.26 10.08-4.88l-4.72-2.23a7.46 7.46 0 0 1-5.45 2.26c-3.52-.11-5.79-1.85-6.47-5.29h18.08c.1-10-5.98-13.32-11.8-13.32-7.24 0-11.66 5.67-11.66 11.84zm11.98-7.14c3.4.13 5.31 2.15 5.98 4.24h-12.3c1.14-2.82 3.1-4.15 6.32-4.24zm-29.65-1.85c.02-.77 0-1.62 0-2.37l-5.6.01v22.47h5.6c0-9.64 0-9.64.03-11.63.11-3.2 1.6-6.37 5.01-6.25 3.4.12 3.68 3.25 3.68 5.68-.03 3.1 0 2.56.04 12.2h5.56c0-9.64 0-9.64-.09-15.68-.1-4.48-3.47-7.27-7.52-7.28-3.21 0-4.79 1.43-6.71 2.85zm-32.53 8.87c0 6.9 4.97 11.74 11.68 11.74 6.7 0 11.76-4.96 11.78-11.74a11.63 11.63 0 0 0-11.78-11.72 11.47 11.47 0 0 0-11.68 11.72zm11.68-6.47c2.63 0 6.12 1.66 6.12 6.47s-3.43 6.47-6.14 6.47c-2.7 0-6.1-1.78-6.11-6.47 0-4.68 3.5-6.48 6.13-6.47zm-60.08 3.7h12.3a6.16 6.16 0 0 0-5.99-4.23c-2.68 0-5.06 1.06-6.31 4.22zm-34.41-3.85c-3.73 0-6.13 2.98-6.13 6.6 0 3.5 2.42 6.63 5.97 6.63 3.85 0 6.3-2.74 6.3-6.71 0-3.61-2.38-6.52-6.14-6.52zm-26.73.02c-3.8 0-6.17 2.84-6.17 6.55 0 4.05 2.6 6.67 6.17 6.67 3.5 0 6.1-2.67 6.1-6.67 0-3.8-2.52-6.55-6.1-6.55zM5.7 0C4.38 0 2.84 0 1.41 1.43 0 2.86 0 4.27 0 5.7v30.45c0 1.37-.06 2.82 1.4 4.26 1.45 1.44 2.92 1.44 4.3 1.44 24 0 35.92-.02 53.9 0l21.12 7.96c0-4.48 0-4.51.04-7.96h14.43c1.4 0 2.88 0 4.3-1.43 1.4-1.42 1.4-2.9 1.4-4.27V5.7c0-1.43-.03-2.86-1.44-4.28C98.04 0 95.89 0 95.19 0Zm22.44 35.63h-5.6v-2.4c-1.63 1.43-3.39 2.96-6.93 2.9-5.5.12-10.72-4.54-10.67-11.98 0-6.64 4.66-11.56 10.67-11.56 3.2.14 4.83 1.14 6.92 3.02V4.54h5.6c.02 10.37.02 20.73 0 31.1zm38.98-22.47h3.33v4.77h-3.33v17.7h-5.6v-17.7h-2.89v-4.77h2.9V4.9h5.6zm-25-.5c3.84 0 5.24 1.33 7.18 2.95v-2.45h5.6l.03 11.23-.03 11.24h-5.6V33.2c-1.58 1.41-3.5 2.93-6.87 2.9-5.09.03-10.25-3.91-10.7-11.32.02-7.65 4.85-12.11 10.39-12.11zM95.63 26H77.55c.75 3.78 3.22 5.19 6.47 5.29a7.5 7.5 0 0 0 5.45-2.26l4.72 2.23c-1.72 2.14-4.02 4.88-9.95 4.88-8 0-12.08-5.79-12.08-11.71.26-7.25 5.32-11.75 11.67-11.75 7.69.06 11.9 5.24 11.8 13.32z" />
		</StyledLogo>
	);
};

const OAuth = ({ getUser }) => {
	const location = useLocation();
	const history = useHistory();
	const language = useSelector((state) => state.global.language);
	const { t } = useTranslation(["userpanel", "landing"]);
	const { getFingerprint } = useFingerprint();
	const dispatch = useDispatch();

	const isAuth = useSelector((state) => state.globalCommon.isAuth);

	const isDarkMode = useSelector(
		(state) => state.globalCommon?.darkMode?.isDarkMode,
	);

	const getUserAfterLogin = () => {
		return getUser().then((res) => {
			const userLanguage = res.payload?.result?.user_language || "pl";
			const frozen = res.payload?.result?.frozen || false;

			dispatch(setIsAuth(true));

			if (language !== userLanguage) {
				dispatch(
					setLanguageAsync({
						language: userLanguage,
						auth: false,
						setCookie: false,
					}),
				);
			}

			if (frozen) {
				history.replace(`${routes.userpanel.path}/settings/account`);
			} else {
				history.replace("/");
			}
		});
	};

	const activateFcmToken = (firebaseHashObject) => {
		const firebaseHash = firebaseHashObject
			? JSON.parse(firebaseHashObject)?.token
			: null;

		return api.put(`/setting/fcm/activate`, {
			id: null,
			hash: firebaseHash ? md5(firebaseHash) : undefined,
		});
	};

	useEffect(() => {
		let oAuth = null;
		if (sessionStorage) {
			const fromsessionStorage = sessionStorage.getItem("oAuth");
			oAuth = fromsessionStorage ? JSON.parse(fromsessionStorage) : {};
		}

		if (!oAuth) {
			console.log("Brak danych w oauth!");
			history.replace("/");
			return;
		}

		const params = queryString.parse(location.search);

		if (location.search.includes("error")) {
			toast.error(t("landing:login.oauthLoginError"));
			if (sessionStorage) {
				sessionStorage.setItem("oAuth", null);
			}
			history.replace(oAuth.pathname);
			return;
		}

		// TODO: sprawdzic czy trzeba drugi raz parsowac
		const firebaseHashObject = oAuth.hashfirebase;
		const firebaseHash = firebaseHashObject
			? JSON.parse(firebaseHashObject)?.token
			: null;

		if (oAuth.initiator === "login" && !isAuth) {
			getFingerprint().then((fingerprintRes) => {
				api.post(`/authorization/oauth?provider=${oAuth.provider}`, {
					token: oAuth.token,
					state: params.state,
					code: params.code,
					fingerprint: fingerprintRes,
				})
					.then((res) => {
						if (
							res?.data?.action_type === "login" &&
							res?.headers?.authorization
						) {
							dispatch(loginGoogle());
							const token =
								res.headers.authorization.match(
									/Bearer\s(\S+)/,
								)[1];
							api.addAuthToken(token);
							dispatch(resetSharedFilters());
							dispatch(resetHomepage());
							dispatch(closeRegistrationVerifyEmailModal());

							getUserAfterLogin();
							if (firebaseHash) {
								activateFcmToken(firebaseHash);
							}
						} else if (res?.data?.action_type === "registration") {
							history.replace({
								pathname: "/",
								state: {
									action: "registerAfterOAuth",
									mail: res.data.email,
									token: oAuth.token,
									provider: oAuth.provider,
								},
							});
							const toastMessage = () => {
								if (oAuth.provider === "google") {
									toast.success(
										t(
											"landing:register.registrationWithGoogleOauth",
										),
									);
								} else if (oAuth.provider === "twitter") {
									toast.success(
										t(
											"landing:register.registrationWithTwitterOauth",
										),
									);
								}
							};
							trackUserRegistrationRedirectAfterOAuth({
								method: "oauth",
								provider: oAuth.provider,
							});
							toastMessage();
						} else if (res?.data?.action_type === "activation") {
							history.replace({
								pathname: "/",
								state: {
									action: "activationOAuth",
									mail: res?.data?.email || null,
									lastSend: res?.data?.last_send || null,
								},
							});
							if (res?.data?.message) {
								toast.success(t(res.data.message));
							}
						}
					})
					.catch((error) => {
						if (error?.response?.data?.error) {
							toast.error(error.response.data.error);
						}
						history.replace("/");
					})
					.finally(() => {
						if (sessionStorage) {
							sessionStorage.setItem("oAuth", null);
						}
					});
			});
		} else if (oAuth.initiator === "connections" && isAuth) {
			api.put("/setting/oauth", {
				token: oAuth.token,
				state: params.state,
				code: params.code,
				provider: oAuth.provider,
			})
				.catch((error) => {
					if (error?.response?.data?.error) {
						toast.error(error.response.data.error);
					}
				})
				.finally(() => {
					if (sessionStorage) {
						sessionStorage.setItem("oAuth", null);
					}
					history.replace(oAuth.pathname);
				});
		} else if (oAuth.initiator === "newPassword" && isAuth) {
			api.post("/setting/create-password", {
				token: oAuth.token,
				state: params.state,
				code: params.code,
				provider: oAuth.provider,
				password: oAuth.password,
			})
				.then(() => {
					toast.success(t("landing:recover.recoverSuccess"));
				})
				.catch((error) => {
					if (error?.response?.data?.error) {
						toast.error(`${error.response.data.error}`);
					}
				})
				.finally(() => {
					if (sessionStorage) {
						sessionStorage.setItem("oAuth", null);
					}
					history.replace(oAuth.pathname);
				});
		} else if (oAuth.initiator === "suspendAccount" && isAuth) {
			api.put("/setting/suspend/oauth", {
				token: oAuth.token,
				state: params.state,
				code: params.code,
				provider: oAuth.provider,
			})
				.catch((error) => {
					if (error?.response?.data?.error) {
						toast.error(`${error.response.data.error}`);
					}
				})
				.finally(() => {
					if (sessionStorage) {
						sessionStorage.setItem("oAuth", null);
					}
					history.replace(oAuth.pathname);
				});
		} else if (oAuth.initiator === "deleteAccount" && isAuth) {
			api.put("/setting/delete/oauth", {
				token: oAuth.token,
				state: params.state,
				code: params.code,
				provider: oAuth.provider,
			})
				.then(() => {
					toast.success(
						t("userpanel:settings.account.deleteSuccess"),
					);
				})
				.catch((error) => {
					if (error?.response?.data?.error) {
						toast.error(`${error.response.data.error}`);
					}
				})
				.finally(() => {
					if (sessionStorage) {
						sessionStorage.setItem("oAuth", null);
					}
					history.replace(oAuth.pathname);
				});
		} else {
			console.log("Brak akcji do wykonania w oauth!");
			if (oAuth?.pathname) {
				history.replace(oAuth.pathname);
			} else {
				history.replace("/");
			}
		}
	}, []);

	return (
		<StyledFallbackContainerOAuth $isDarkMode={isDarkMode}>
			<StyledMessageContainerOAuth>
				<div style={{ marginBottom: "2rem" }}>
					<DatezoneLogoOAuth isDarkMode={isDarkMode} />
				</div>
				<LoaderOAuth size={50} isDarkMode={isDarkMode} />
			</StyledMessageContainerOAuth>
		</StyledFallbackContainerOAuth>
	);
};

export default connect(null, {
	getUser,
})(OAuth);
